import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  authApi,
  ApiError,
  OAuth2CallbackResponse,
  OAuth2User,
} from '../utils/api';
import { redirectTo } from '../utils/navigation';

interface AuthContextType {
  user: OAuth2User | null;
  token: string | null;
  loading: boolean;
  accessDenied: boolean;
  isGlobalAdmin: boolean;
  organizationCodes: string[];
  initiateLogin: () => Promise<void>;
  handleOAuth2Callback: (
    code: string,
    state: string,
  ) => Promise<OAuth2CallbackResponse>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Module-level flag to track initialization (persists across StrictMode remounts)
let authInitialized = false;

const STORAGE_KEYS = {
  TOKEN: 'admin_oauth_token',
  USER: 'admin_oauth_user',
  STATE: 'oauth_state',
  CODE_VERIFIER: 'oauth_code_verifier',
  IS_GLOBAL_ADMIN: 'admin_is_global_admin',
  ORGANIZATION_CODES: 'admin_organization_codes',
};

/** Reads a JSON-encoded storage value, treating corrupt content as absent: the
 *  state initializers run during render, so a throw here would white-screen the
 *  app instead of starting an unauthenticated session. */
const readJson = <T,>(key: string, fallback: T): T => {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize state synchronously from localStorage to avoid flash of unauthenticated state
  const [user, setUser] = useState<OAuth2User | null>(() =>
    readJson<OAuth2User | null>(STORAGE_KEYS.USER, null),
  );
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  });
  // If we have a token, we're still loading (need to validate). If no token, not loading.
  const [loading, setLoading] = useState(() => {
    return !!localStorage.getItem(STORAGE_KEYS.TOKEN);
  });
  const [accessDenied, setAccessDenied] = useState(false);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.IS_GLOBAL_ADMIN) === 'true';
  });
  const [organizationCodes, setOrganizationCodes] = useState<string[]>(() =>
    readJson<string[]>(STORAGE_KEYS.ORGANIZATION_CODES, []),
  );
  const navigate = useNavigate();
  const validationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Ref to store the latest validateToken callback to avoid stale closures
  const validateTokenRef = useRef<((token: string) => Promise<boolean>) | null>(null);

  const clearAuth = useCallback(() => {
    setToken(null);
    setUser(null);
    setAccessDenied(false);
    setIsGlobalAdmin(false);
    setOrganizationCodes([]);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.STATE);
    localStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
    localStorage.removeItem(STORAGE_KEYS.IS_GLOBAL_ADMIN);
    localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_CODES);
  }, []);

  /**
   * Revalidates a stored token. Returns `true` when the session must be kept —
   * either the token is valid, or validation failed for a transient reason
   * (5xx, gateway error, network failure) that says nothing about the token.
   * Returns `false` only for a definitive rejection.
   */
  const validateToken = useCallback(
    async (tokenToValidate: string) => {
      try {
        // Use the new endpoint that validates token AND returns admin status
        const result = await authApi.validateOAuth2Token(tokenToValidate);

        // Update user info
        setUser({
          sub: result.sub,
          email: result.email,
          name: result.name,
        });

        // Update global admin status and org codes
        setIsGlobalAdmin(result.isGlobalAdmin ?? false);
        setOrganizationCodes(result.organizationCodes ?? []);
        localStorage.setItem(STORAGE_KEYS.IS_GLOBAL_ADMIN, String(result.isGlobalAdmin ?? false));
        localStorage.setItem(STORAGE_KEYS.ORGANIZATION_CODES, JSON.stringify(result.organizationCodes ?? []));

        // Check admin status - token is valid but user may not be admin
        if (!result.isAdmin) {
          // Same verdict as the callback path: drop the session and record the
          // access-denied state so ProtectedRoute sends them to /access-denied
          // rather than keeping a non-admin token in storage.
          clearAuth();
          setAccessDenied(true);
          return false;
        }

        // Token is valid and user is admin
        setAccessDenied(false);
        return true;
      } catch (error) {
        // Only a definitive auth rejection (401) ends the session: the token is
        // invalid or expired. Anything else (5xx, gateway error, network
        // failure) is transient - the token may still be valid, so the session
        // is kept and the user is not bounced to /login mid-work.
        if (error instanceof ApiError && error.status === 401) {
          clearAuth();
          // Use window.location.pathname to avoid dependency on location from useLocation()
          // This prevents the callback from being recreated on navigation
          const currentPath = window.location.pathname;
          if (
            currentPath !== '/login' &&
            currentPath !== '/auth/callback' &&
            currentPath !== '/access-denied'
          ) {
            navigate('/login');
          }
          return false;
        }
        return true;
      }
    },
    [clearAuth, navigate],
  );

  // Keep the ref updated with the latest callback version
  validateTokenRef.current = validateToken;

  useEffect(() => {
    // Prevent initAuth from running multiple times (even across StrictMode remounts)
    if (authInitialized) {
      return;
    }
    authInitialized = true;

    const initAuth = async () => {
      // Skip validation on access-denied page to prevent redirect loops
      if (window.location.pathname === '/access-denied') {
        setLoading(false);
        return;
      }

      // Token/user are already loaded from localStorage in useState initializers
      const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);

      if (storedToken) {
        // Validate the token asynchronously. validateToken owns the cleanup for
        // both failure modes (401 -> clearAuth, non-admin -> accessDenied), so a
        // second clearAuth here would erase the access-denied verdict and send a
        // non-admin to /login instead of /access-denied.
        await validateTokenRef.current?.(storedToken);
      }

      setLoading(false);
    };

    initAuth();

    const intervalId = setInterval(() => {
      // Don't run periodic validation on access-denied page
      if (window.location.pathname === '/access-denied') {
        return;
      }
      const currentToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (currentToken) {
        // Use ref to get latest callback version
        validateTokenRef.current?.(currentToken);
      }
    }, 60000);

    validationIntervalRef.current = intervalId;

    return () => {
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initiateLogin = async () => {
    try {
      const { authorization_url, state, code_verifier } =
        await authApi.getAuthorizationUrl();

      localStorage.setItem(STORAGE_KEYS.STATE, state);
      localStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, code_verifier);

      redirectTo(authorization_url);
    } catch (error) {
      console.error('Failed to initiate login:', error);
      throw error;
    }
  };

  const handleOAuth2Callback = useCallback(
    async (code: string, state: string): Promise<OAuth2CallbackResponse> => {
      const storedState = localStorage.getItem(STORAGE_KEYS.STATE);
      const codeVerifier = localStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);

      if (!storedState || state !== storedState) {
        throw new Error('Invalid state parameter');
      }

      if (!codeVerifier) {
        throw new Error('Missing code verifier');
      }

      // The PKCE material is one-shot, but it must survive a failed exchange:
      // deleting it up front makes any retry of the same callback URL fail with
      // 'Invalid state parameter'. A thrown error therefore leaves both keys in
      // place; every answered exchange (success or access-denied) clears them.
      const response = await authApi.handleOAuth2Callback(
        code,
        state,
        codeVerifier,
      );

      localStorage.removeItem(STORAGE_KEYS.STATE);
      localStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);

      // A session is only handed out to an admin. `isAdmin: false` is the
      // backend's access-denied verdict, and `isAuthenticated` alone gates
      // ProtectedRoute, so storing that token would let a non-admin into /admin.
      if (response.error === 'access_denied' || response.isAdmin !== true) {
        clearAuth();
        setAccessDenied(true);
        setLoading(false);
        return response;
      }

      if (response.access_token && response.user) {
        setToken(response.access_token);
        setUser(response.user);

        localStorage.setItem(STORAGE_KEYS.TOKEN, response.access_token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));

        // Store global admin status and org codes
        const globalAdmin = response.isGlobalAdmin ?? false;
        const orgCodes = response.organizationCodes ?? [];
        setIsGlobalAdmin(globalAdmin);
        setOrganizationCodes(orgCodes);
        localStorage.setItem(STORAGE_KEYS.IS_GLOBAL_ADMIN, String(globalAdmin));
        localStorage.setItem(STORAGE_KEYS.ORGANIZATION_CODES, JSON.stringify(orgCodes));
      }

      setAccessDenied(false);

      // Ensure loading is set to false after callback completes
      setLoading(false);

      return response;
    },
    [clearAuth],
  );

  const logout = async () => {
    try {
      if (token) {
        await authApi.oauth2Logout(token);
      }
    } catch (error) {
      // The backend may answer 503 when the identity provider is down; the
      // local session is cleared regardless so the user is never left logged in.
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      navigate('/login');
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    accessDenied,
    isGlobalAdmin,
    organizationCodes,
    initiateLogin,
    handleOAuth2Callback,
    logout,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
